import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { setAccessToken } from '@/lib/session';
import { getApiErrorMessage } from '@/lib/http';
import { iamApi } from '../services/iam.api';

type Status =
    | 'loading'
    | 'needs-otp-request'
    | 'needs-otp-verify'
    | 'valid'
    | 'accepting'
    | 'accepted'
    | 'error';

interface InviteData {
    email: string;
    role?: { name?: string; scope?: string };
}

export default function AcceptInvitePage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { user, ready, loading: authLoading, init } = useAuth();

    const [status, setStatus] = useState<Status>('loading');
    const [inviteData, setInviteData] = useState<InviteData | null>(null);
    const [error, setError] = useState('');
    const [otp, setOtp] = useState('');
    const [isRequestingOtp, setIsRequestingOtp] = useState(false);
    const [countdown, setCountdown] = useState(0);

    // Fetch invite data once per token. Don't include auth state in the deps:
    // after a successful accept the token is consumed on the backend, and
    // re-validating would flash an "Invite Invalid" screen before the redirect.
    useEffect(() => {
        if (!token) {
            setStatus('error');
            setError('No invite token provided');
            return;
        }

        iamApi
            .validateInvite(token)
            .then(({ data }) => setInviteData(data))
            .catch((err) => {
                setStatus('error');
                setError(err.response?.data?.error || 'Invalid or expired invite');
            });
    }, [token]);

    // Drive the visible status from auth state, but never overwrite the
    // accept-flow states ('accepting' / 'accepted' / 'error' / 'needs-otp-verify').
    useEffect(() => {
        if (!inviteData) return;
        setStatus((prev) => {
            if (
                prev === 'accepting' ||
                prev === 'accepted' ||
                prev === 'error' ||
                prev === 'needs-otp-verify'
            ) {
                return prev;
            }
            if (authLoading || !ready) return 'loading';
            return user ? 'valid' : 'needs-otp-request';
        });
    }, [inviteData, user, ready, authLoading]);

    useEffect(() => {
        if (countdown <= 0) return undefined;
        const timer = window.setInterval(() => {
            setCountdown((value) => Math.max(0, value - 1));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [countdown]);

    const handleAccept = async () => {
        if (!token) return;
        setStatus('accepting');
        try {
            await iamApi.acceptInvite(token);
            await init();
            setStatus('accepted');
            window.setTimeout(() => {
                navigate('/', { replace: true });
            }, 1200);
        } catch (err: any) {
            setStatus('error');
            setError(err.response?.data?.error || 'Failed to accept invite');
        }
    };

    const handleRequestOtp = async () => {
        if (!token) return;
        setIsRequestingOtp(true);
        setError('');
        try {
            await iamApi.requestInviteOtp(token);
            setStatus('needs-otp-verify');
            setCountdown(60);
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to send verification code.'));
        } finally {
            setIsRequestingOtp(false);
        }
    };

    const handleVerifyOtp = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!token || otp.length !== 6) return;
        setStatus('accepting');
        setError('');
        try {
            const { data } = await iamApi.verifyInviteOtpAndAccept(token, otp);
            setAccessToken(data.accessToken);
            await init();
            setStatus('accepted');
            window.setTimeout(() => {
                navigate('/', { replace: true });
            }, 1200);
        } catch (err: any) {
            setStatus('needs-otp-verify');
            setError(getApiErrorMessage(err, 'Invalid code. Please try again.'));
        }
    };

    if (status === 'loading') {
        return (
            <div className="w-full min-h-[80vh] flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="w-full min-h-[80vh] flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-100 text-red-600 mb-4">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Invite Invalid</h1>
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    if (status === 'accepted') {
        return (
            <div className="w-full min-h-[80vh] flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-green-100 text-green-600 mb-4">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">You're in!</h1>
                    <p className="text-sm text-gray-500 mb-6">
                        You've joined as <strong>{inviteData?.role?.name}</strong>. Redirecting…
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'needs-otp-request' || status === 'needs-otp-verify') {
        return (
            <div className="w-full min-h-[80vh] flex items-center justify-center px-4">
                <div className="w-full max-w-md text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 mb-4">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">You've been invited!</h1>
                    {inviteData ? (
                        <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4 mb-6 text-left">
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Email</span>
                                    <span className="font-medium text-gray-900">{inviteData.email}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Role</span>
                                    <span className="font-medium text-gray-900">{inviteData.role?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Scope</span>
                                    <span className="font-medium text-gray-900">{inviteData.role?.scope}</span>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {status === 'needs-otp-request' ? (
                        <>
                            <p className="text-sm text-gray-500 mb-4">
                                We'll send a 6-digit code to <strong>{inviteData?.email}</strong>.
                            </p>
                            <button
                                type="button"
                                onClick={() => void handleRequestOtp()}
                                disabled={isRequestingOtp}
                                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {isRequestingOtp ? 'Sending…' : 'Send verification code'}
                            </button>
                            {error ? <p className="text-sm text-red-600 mt-3">{error}</p> : null}
                        </>
                    ) : (
                        <form onSubmit={handleVerifyOtp} className="space-y-3">
                            <p className="text-sm text-gray-500 mb-2">
                                Enter the 6-digit code we sent to <strong>{inviteData?.email}</strong>.
                            </p>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={otp}
                                onChange={(event) =>
                                    setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))
                                }
                                placeholder="123456"
                                autoFocus
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                                type="submit"
                                disabled={otp.length !== 6}
                                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                Verify and accept
                            </button>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                                <button
                                    type="button"
                                    className="text-indigo-600 hover:underline disabled:opacity-50 disabled:no-underline disabled:text-gray-400"
                                    disabled={countdown > 0 || isRequestingOtp}
                                    onClick={() => void handleRequestOtp()}
                                >
                                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                                </button>
                                <span>Check your spam folder if it's missing.</span>
                            </div>
                            {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
                        </form>
                    )}
                </div>
            </div>
        );
    }

    // status === 'valid' or 'accepting' (logged-in user accepting an invite for their account)
    const emailMismatch =
        user && inviteData && user.email.toLowerCase() !== inviteData.email.toLowerCase();

    return (
        <div className="w-full min-h-[80vh] flex items-center justify-center px-4">
            <div className="w-full max-w-md text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 mb-4">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                </div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">You've been invited!</h1>
                {inviteData ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4 mb-6 text-left">
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Invite for</span>
                                <span className="font-medium text-gray-900">{inviteData.email}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Role</span>
                                <span className="font-medium text-gray-900">{inviteData.role?.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Scope</span>
                                <span className="font-medium text-gray-900">{inviteData.role?.scope}</span>
                            </div>
                        </div>
                    </div>
                ) : null}

                {emailMismatch ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-left">
                        <p className="text-sm text-amber-900">
                            <strong>Heads up:</strong> this invite was sent to{' '}
                            <strong>{inviteData?.email}</strong> but you're signed in as{' '}
                            <strong>{user?.email}</strong>. Accepting will attach the role to your current
                            account. If you meant to use a different account,{' '}
                            <Link to="/login" className="underline">
                                sign in with that email
                            </Link>{' '}
                            first.
                        </p>
                    </div>
                ) : null}

                <button
                    onClick={handleAccept}
                    disabled={status === 'accepting'}
                    className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                    {status === 'accepting' ? 'Accepting…' : 'Accept Invite'}
                </button>
            </div>
        </div>
    );
}
